// src/stores/useMessageThreadStore.ts
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import type { MessageThread, ReplyWarning, ThreadMessage } from '../types/message';
import {
  loadMessageThreadApi,
  resolveMessageThreadApi,
  submitReplyApi,
  verifyDateOfBirthApi,
} from '../services/message.service';
import { useApiLoading } from '../lib/composables/useApiLoading';

export const useMessageThreadStore = defineStore('message-thread', () => {
  const { apiLoading, setApiLoading, setApiDone } = useApiLoading();

  const messageId = ref<string | null>(null);
  const firstName = ref<string | null>(null);
  const messages = ref<ThreadMessage[]>([]);
  const canReply = ref(false);
  const replyWarning = ref<ReplyWarning | null>(null);
  const isVerified = ref(false);
  // ENG-21791 which message the endpoint has confirmed access to, so we probe once
  const resolvedFor = ref<string | null>(null);
  const replyMessage = ref('');
  const attachments = ref<File[]>([]);

  // A thread is a conversation once there is more than one message, or once the
  // patient can add to it. No expiry clock of its own — the link's lifetime is
  // server-side and the client-side timeout is useSessionStore's idle handling.
  const showConversationView = computed(() => messages.value.length > 1 || canReply.value);

  const latestMessageId = computed(() => messages.value.at(-1)?.id ?? null);

  const setMessageId = (id: string) => (messageId.value = id);

  const applyThread = (thread: MessageThread) => {
    messageId.value = thread.messageId;
    firstName.value = thread.firstName;
    messages.value = thread.messages ?? [];
    canReply.value = thread.canReply;
    replyWarning.value = thread.replyWarning;
  };

  async function loadThread(id: string = messageId.value ?? '') {
    if (!id) return { ok: false as const };

    setApiLoading();
    try {
      applyThread(await loadMessageThreadApi(id));
      return { ok: true as const };
    } finally {
      setApiDone();
    }
  }

  // ENG-21791 "may this session read the thread" — the guard routes on the outcome
  async function resolveThread(id: string = messageId.value ?? '') {
    if (!id) return { status: 'error' as const };
    if (resolvedFor.value === id && isVerified.value) return { status: 'ok' as const };

    setApiLoading();
    try {
      const result = await resolveMessageThreadApi(id);
      resolvedFor.value = id;
      messageId.value = id;

      if (result.status === 'ok') {
        applyThread(result.thread);
        isVerified.value = true;
      } else {
        isVerified.value = false;
        resolvedFor.value = null;
      }

      return result.status === 'ok'
        ? ({ status: 'ok' } as const)
        : result.status === 'unverified'
          ? ({ status: 'unverified' } as const)
          : ({ status: 'error', code: result.code } as const);
    } finally {
      setApiDone();
    }
  }

  async function verifyDateOfBirth(dateOfBirth: string) {
    if (!messageId.value) return { ok: false as const };

    setApiLoading();
    try {
      const result = await verifyDateOfBirthApi(messageId.value, dateOfBirth);
      isVerified.value = result.ok;
      return result;
    } finally {
      setApiDone();
    }
  }

  async function submitReply() {
    // A double-click fires two replies; guarding on the in-flight flag stops the
    // second call duplicating the first.
    if (!messageId.value || apiLoading.value) return { ok: false as const };

    setApiLoading();
    try {
      return await submitReplyApi(messageId.value, replyMessage.value, attachments.value);
    } finally {
      setApiDone();
    }
  }

  function reset() {
    messageId.value = null;
    firstName.value = null;
    messages.value = [];
    canReply.value = false;
    replyWarning.value = null;
    isVerified.value = false;
    replyMessage.value = '';
    attachments.value = [];
  }

  return {
    apiLoading,
    messageId,
    firstName,
    messages,
    canReply,
    replyWarning,
    isVerified,
    replyMessage,
    attachments,
    showConversationView,
    latestMessageId,
    setMessageId,
    loadThread,
    resolveThread,
    verifyDateOfBirth,
    submitReply,
    reset,
  };
});
